import type { ReactNode } from "react";
import { Box, Typography } from "@mui/material";
const THUMB_COL_W = 72;
const COLLAGE_GAP = 6;
export const COLLAGE_H = 200;
const SINGLE_IMAGE_MAX_H = { xs: 250, sm: 280 } as const;

/** Single-image layout — step cards use fixed height; spec diagrams stay intrinsic. */
export type ImageCollageSingleLayout = "intrinsic" | "fixed";

/** Generic image item for collage presentation (domain-agnostic). */
export interface ImageCollageItem {
  src: string;
  alt?: string;
}

function PlusBadge({
  label,
  onClick,
}: {
  label: string;
  onClick?: (e: React.MouseEvent) => void;
}) {
  const interactive = Boolean(onClick);
  return (
    <Box
      component={interactive ? "button" : "span"}
      type={interactive ? "button" : undefined}
      onClick={onClick}
      aria-label={label}
      sx={{
        position: "absolute",
        right: 8,
        bottom: 8,
        minWidth: 32,
        height: 28,
        px: 1,
        border: "none",
        borderRadius: 999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "rgba(0,0,0,0.62)",
        color: "common.white",
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1,
        zIndex: 3,
        cursor: interactive ? "pointer" : "default",
        ...(interactive
          ? {
              "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: "primary.main",
                outlineOffset: 2,
              },
            }
          : { pointerEvents: "none" }),
      }}
    >
      {label}
    </Box>
  );
}

function TapToOpenOverlay({ label, compact }: { label: string; compact?: boolean }) {
  return (
    <Box
      aria-hidden
      data-testid="image-collage-tap-overlay"
      sx={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        boxSizing: "border-box",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-end",
        textAlign: "center",
        px: compact ? 1 : 1.25,
        py: compact ? 0.375 : 0.75,
        ...(compact
          ? {
              height: 28,
              background:
                "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.28) 65%, transparent 100%)",
            }
          : {
              background: "linear-gradient(transparent, rgba(0,0,0,0.72))",
            }),
        pointerEvents: "none",
        zIndex: 2,
      }}
    >
      <Typography
        component="span"
        sx={{
          color: "common.white",
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1.2,
          textAlign: "center",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function CollageTile({
  src,
  onOpen,
  ariaLabel,
  sx,
  badge,
  tapToOpenLabel,
  compactOverlay,
  intrinsic,
}: {
  src: string;
  onOpen: (src: string) => void;
  ariaLabel: string;
  sx?: object;
  badge?: ReactNode;
  /** Bottom gradient overlay — only on the main / single image. */
  tapToOpenLabel?: string;
  compactOverlay?: boolean;
  /** Size to natural image dimensions (single-image mode). */
  intrinsic?: boolean;
}) {
  if (!src) return null;

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(src)}
      aria-label={ariaLabel}
      sx={{
        position: "relative",
        p: 0,
        border: "none",
        borderRadius: 1.25,
        overflow: "hidden",
        bgcolor: "#ffffff",
        cursor: "pointer",
        display: intrinsic ? "inline-block" : "block",
        width: intrinsic ? "fit-content" : "100%",
        maxWidth: "100%",
        height: intrinsic ? "auto" : "100%",
        verticalAlign: intrinsic ? "top" : undefined,
        mx: intrinsic ? "auto" : undefined,
        boxShadow: "inset 0 0 0 1px rgba(28,46,54,0.06)",
        "&:hover .image-collage-img": {
          transform: "scale(1.02)",
        },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
        ...sx,
      }}
    >
      <Box
        component="img"
        className="image-collage-img"
        src={src}
        alt=""
        sx={{
          width: intrinsic ? "auto" : "100%",
          maxWidth: "100%",
          height: intrinsic ? "auto" : "100%",
          maxHeight: intrinsic ? SINGLE_IMAGE_MAX_H : undefined,
          objectFit: "contain",
          objectPosition: "center",
          display: "block",
          transition: "transform 0.22s ease",
        }}
      />

      {tapToOpenLabel ? (
        <TapToOpenOverlay label={tapToOpenLabel} compact={compactOverlay} />
      ) : null}
      {badge}
    </Box>
  );
}

/**
 * Generic multi-image collage (domain-agnostic).
 *
 * Layout by count:
 * 1  → intrinsic image (default) or fixed COLLAGE_H preview when singleImageLayout="fixed"
 * 2  → single large image + +1 badge
 * 3  → large left + two stacked thumbs
 * 4+ → same as 3, bottom thumb shows +(total − 2)
 */
export function ImageCollage({
  images,
  onOpen,
  tapToOpenLabel,
  morePhotosLabel,
  zoomLabel,
  compactRoot = false,
  singleImageLayout = "intrinsic",
}: {
  images: ImageCollageItem[];
  onOpen: (src: string) => void;
  tapToOpenLabel: string;
  morePhotosLabel: (count: number) => string;
  zoomLabel: string;
  /** Prevent the collage from stretching to fill a tall flex/grid parent. */
  compactRoot?: boolean;
  /** Step cards: fixed COLLAGE_H band with object-fit contain (no crop). */
  singleImageLayout?: ImageCollageSingleLayout;
}): JSX.Element | null {
  const items = images.filter((item) => Boolean(item.src));
  if (!items.length) return null;

  const wrapCompactRoot = (node: JSX.Element): JSX.Element =>
    compactRoot ? (
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          height: "fit-content",
          alignSelf: "flex-start",
          flexShrink: 0,
        }}
      >
        {node}
      </Box>
    ) : (
      node
    );

  const main = items[0]!;
  const mainSrc = main.src;

  // 1 image — intrinsic (spec) or fixed-height preview (step cards)
  if (items.length === 1) {
    if (singleImageLayout === "fixed") {
      // Overlay sits on the fixed preview (not the painted bitmap) so portrait
      // letterboxing never shrinks the "tap to open" bar — matches 2-image Step tiles.
      return wrapCompactRoot(
        <Box
          data-testid="image-collage-fixed-preview"
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: "100%",
            height: COLLAGE_H,
          }}
        >
          <CollageTile
            src={mainSrc}
            onOpen={onOpen}
            ariaLabel={zoomLabel}
            sx={{ width: "100%", height: "100%", display: "block" }}
          />
          <TapToOpenOverlay label={tapToOpenLabel} />
        </Box>
      );
    }

    return wrapCompactRoot(
      <Box sx={{ width: "fit-content", maxWidth: "100%", height: "fit-content" }}>
        <CollageTile
          src={mainSrc}
          onOpen={onOpen}
          ariaLabel={zoomLabel}
          tapToOpenLabel={tapToOpenLabel}
          compactOverlay
          intrinsic
        />
      </Box>
    );
  }

  // 2 images — primary only + +1 badge
  if (items.length === 2) {
    return wrapCompactRoot(
      <Box sx={{ width: "100%", maxWidth: "100%", height: COLLAGE_H }}>
        <CollageTile
          src={mainSrc}
          onOpen={() => onOpen(mainSrc)}
          ariaLabel={zoomLabel}
          tapToOpenLabel={tapToOpenLabel}
          badge={
            <PlusBadge
              label={morePhotosLabel(1)}
              onClick={(e) => {
                e.stopPropagation();
                onOpen(mainSrc);
              }}
            />
          }
        />
      </Box>
    );
  }

  // 3+ images — large left + two stacked thumbs
  // Overflow badge on bottom thumb only when 4+: N = totalImages - 2
  const showOverflow = items.length > 3;
  const overflowCount = items.length - 2;

  return wrapCompactRoot(
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: `minmax(0, 1fr) min(${THUMB_COL_W}px, 28%)`,
          sm: `1fr ${THUMB_COL_W}px`,
        },
        gridTemplateRows: `${(COLLAGE_H - COLLAGE_GAP) / 2}px ${(COLLAGE_H - COLLAGE_GAP) / 2}px`,
        gap: `${COLLAGE_GAP}px`,
        width: "100%",
        maxWidth: "100%",
        height: COLLAGE_H,
        minWidth: 0,
      }}
    >
      <CollageTile
        src={mainSrc}
        onOpen={onOpen}
        ariaLabel={zoomLabel}
        tapToOpenLabel={tapToOpenLabel}
        sx={{ gridRow: "1 / 3", minHeight: 0, minWidth: 0 }}
      />
      <CollageTile
        src={items[1]!.src}
        onOpen={onOpen}
        ariaLabel={zoomLabel}
        sx={{ minHeight: 0, minWidth: 0 }}
      />
      <CollageTile
        src={items[2]!.src}
        onOpen={onOpen}
        ariaLabel={zoomLabel}
        sx={{ minHeight: 0, minWidth: 0 }}
        badge={
          showOverflow ? (
            <PlusBadge
              label={morePhotosLabel(overflowCount)}
              onClick={(e) => {
                e.stopPropagation();
                onOpen(mainSrc);
              }}
            />
          ) : undefined
        }
      />
    </Box>
  );
}
